import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (token) {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('admin_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      
      // Handle the response based on your backend structure
      if (response.token || response.accessToken) {
        const token = response.token || response.accessToken;
        localStorage.setItem('admin_token', token);
        
        // Set user data - handle different response structures
        const userData = response.user || response.admin || { 
          name: response.name || 'Admin', 
          email: credentials.email,
          id: response.id || 'admin'
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true };
      } else if (response.success && response.data) {
        // Alternative response structure
        const token = response.data.token || response.data.accessToken;
        if (token) {
          localStorage.setItem('admin_token', token);
          setUser(response.data.user || response.data.admin || { 
            name: 'Admin', 
            email: credentials.email 
          });
          setIsAuthenticated(true);
          return { success: true };
        }
      }
      
      return { success: false, error: 'Invalid response from server' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
